import { useLayoutEffect } from "react";
import styled from "styled-components";
import Footer from "../../components/fragments/Footer";
import NavBar from "../../components/fragments/NavBar";
import HeroBanner from "../../components/fragments/HeroBanner";
import { size } from "../../components/styles/Breakpoints";

import { Container } from "../../components/styles/Layout";

import { useStaticSeo } from "../../hooks/useSeo";
import { STATIC_SEO } from "../../data/seo";

const Wrapper = styled.section`
	margin-bottom: 4rem;
`;
const BigTitle = styled.h2``;
const TinyTitle = styled.h4``;
const Text = styled.p`
	max-width: 40rem;
`;
const ListWrap = styled.ul``;
const ListItem = styled.li`
	font-size: 0.9rem;
	line-height: 1.5;
	font-weight: 300;
`;

export default function PrivacyPolicy() {
    useStaticSeo(STATIC_SEO["/privacy-policy"]);
	useLayoutEffect(() => {
		document.getElementById("root").scrollTo(0, 0);
	}, []);

	return (
		<>
			<NavBar location="public" pageTitle="Privacy Policy" />
			<HeroBanner
				title="Privacy Policy"
				background="/images/pp-outside-dark.jpg"
				showRating={false}
			/>
			<Wrapper>
				<Container>
					<Text>
						We respect your privacy, and want to be clear about how we handle information about you.
					</Text>
					<TinyTitle>What information may we collect about you?</TinyTitle>
					<Text>
						We may collect personal data about you in the course of our work. That may include
						things like:
					</Text>
					<ListWrap>
						<ListItem>Your contact details.</ListItem>
						<ListItem>Your date of birth.</ListItem>
						<ListItem>Where you work.</ListItem>
						<ListItem>Your wishes and criteria in any transaction you are considering.</ListItem>
						<ListItem>
							Financial information, for example about your income and capital resources.
						</ListItem>
					</ListWrap>
					<Text>
						Usually we will have received this directly from you. However, we may also receive
						personal data about you from others, such as from the other party to a transaction you
						are engaged in or from joint agents. Family members also sometimes provide information
						about each other.
					</Text>
					<TinyTitle>How do we use your information?</TinyTitle>
					<Text>Our use of such personal data is subject to data protection law.</Text>
					<Text>
						We mainly use your personal data to provide you with information or services you have
						requested. We may also use it for any other purpose for which you give your consent. For
						example we may send you additional information about the firm or its services, if you
						have consented to us doing so.
					</Text>
					<Text>
						We may also use it for other normal purposes connected with our work. For example we
						will use your information to update our own business records, complete statutory
						returns, and otherwise comply with our regulatory obligations.
					</Text>
					<TinyTitle>Will we share information about you with anyone?</TinyTitle>
					<Text>
						We take your privacy seriously. We will never sell your personal data to anyone, and we
						take precautions to keep it secure.
					</Text>
					<Text>
						It will sometimes be a normal and necessary part of our work to pass on information to
						third parties. For example:
					</Text>
					<ListWrap>
						<ListItem>
							We may pass on information about you to solicitors to help them proceed with a
							transaction in which you are involved.
						</ListItem>
						<ListItem>
							If you are a prospective tenant, we may pass on references about you to the landlord.
						</ListItem>
						<ListItem>
							We may inform utility companies of changes in the occupiers of property.
						</ListItem>
						<ListItem>
							We may pass on information to maintenance and repair contractors, in respect of
							property where we have responsibilities.
						</ListItem>
						<ListItem>
							We may need to pass on information to those who help collect outstanding accounts.
						</ListItem>
					</ListWrap>
					<Text>
						In addition our practice may be audited or checked by third parties such as our
						accountants, which may enable them to see some information about you. Such third parties
						are required to maintain confidentiality in relation to your information.
					</Text>
					<TinyTitle>Using our free property report</TinyTitle>
					<Text>
						Our <a href="/property-report">property report</a> lets anyone look up information
						about a home by entering a postcode. You do not need an account, and we do not ask for
						your name or contact details to use it.
					</Text>
					<Text>
						When you run a report, we receive the postcode you enter and, if you choose one, the
						address you select. We use these only to produce your report. To do that, our server
						sends the postcode to postcodes.io, and the postcode and certificate reference to the
						government&rsquo;s Energy Performance Certificate register. If you ask for the
						transport, shops, crime or planning panels, it also sends the map coordinates of the postcode to
						Transport for London, to the Food Standards Agency, to the Home Office’s police
						data service and to the Planning Data Platform. Everything else in the report
						comes from copies of public data we hold ourselves, so no request leaves our server
						for it. We do not pass on your IP address or anything else about you to any of these
						services.
					</Text>
					<Text>
						The report lets you choose which sections you want to see. Your choice is remembered
						in your own browser&rsquo;s local storage so you do not have to set it again on your
						next visit. It stays on your device, is never sent to us, and holds nothing but the
						list of sections you ticked. Clearing your browsing data removes it, and the report
						works normally if your browser blocks it.
					</Text>
					<Text>
						We do not keep a record of the reports you run, link them to you, or use them for
						marketing. Our web server&rsquo;s security log records the IP address and web address of
						each request, which for a report includes the postcode and any address you selected.
						This log is deleted after 14 days. We also
						briefly hold your IP address, for up to one minute, to prevent the service being
						overloaded by automated requests.
					</Text>
					<TinyTitle>Information about properties shown in the report</TinyTitle>
					<Text>
						The report displays information that government bodies already publish about
						individual homes:
					</Text>
					<ListWrap>
						<ListItem>
							Energy performance certificates, from the Ministry of Housing, Communities and Local
							Government&rsquo;s EPC register.
						</ListItem>
						<ListItem>Sale prices and dates, from HM Land Registry&rsquo;s Price Paid data.</ListItem>
						<ListItem>
							Council tax, broadband and area information, which relates to areas rather than
							individual people.
						</ListItem>
					</ListWrap>
					<Text>
						Because this information is tied to an address, it can be personal data about the people
						who live there. We do not add names or any other information about occupants, and we
						do not use it to contact anyone. We display it because we have a legitimate interest in
						helping people understand homes on the sales and lettings market, including how energy
						efficient they are.
					</Text>
					<Text>
						If your home appears in the report and you object to us showing its information, please
						contact us using the details below and we will consider your request. Please be aware
						that the same information is also published by the government bodies listed above, so
						removing it from our report does not remove it from the original source.
					</Text>
					<TinyTitle>Your rights</TinyTitle>
					<Text>
						You have a right of access under data protection law to the personal data that we hold
						about you. We seek to keep that personal data correct and up to date. You should let us
						know if you believe the information we hold about you needs to be corrected or updated.
					</Text>
					<Text>
						You also have the right to object to our use of your personal data where we rely on
						legitimate interests, and to ask us to erase or restrict it in some circumstances.
					</Text>
					<Text>
						If you are unhappy with how we have handled your personal data, you can complain to the
						Information Commissioner&rsquo;s Office at{" "}
						<a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer">
							ico.org.uk
						</a>
						. We would appreciate the chance to deal with your concerns first.
					</Text>
					<Text>
						The person responsible for data protection at this organisation and whom you should
						contact is Asad Jamil – 204 Tooting High Street SW170DG,
						<a href="mailto:asad@property-people.co.uk"> asad@property-people.co.uk</a>
					</Text>
					<Text>Last reviewed September 2026</Text>
				</Container>
			</Wrapper>
			<Footer />
		</>
	);
}
